<template>
  <div class="p-3 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4">
      <div>
        <h1 class="text-xl font-bold text-gray-800 dark:text-white">Gestión de Productos</h1>
        <p class="text-xs text-gray-600 dark:text-gray-400">Administra el inventario</p>
      </div>
      <div class="flex items-center gap-2">
        <button v-if="filteredProductos.length > 0" @click="abrirModalRecalcularMasivo" class="flex items-center px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-xs font-semibold shadow hover:shadow-md">
          <Icon icon="mdi:calculator" class="mr-1.5" />
          <span>Recalcular</span>
        </button>
        <button @click="openCreateModal" class="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-xs font-semibold shadow hover:shadow-md">
          <Icon icon="mdi:plus" class="mr-1.5" />
          <span>Nuevo</span>
        </button>
      </div>
    </div>

    <!-- LECTOR DE CÓDIGO - BÚSQUEDA RÁPIDA -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3">
        <div class="flex-1 relative">
          <Icon icon="mdi:barcode-scan" class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-xl" />
          <input 
            ref="lectorInput"
            v-model="codigoEscaneado"
            type="text"
            placeholder="Escanea o ingresa el código del producto..."
            @keyup.enter="buscarProductoPorCodigo"
            class="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-2 border-blue-200 dark:border-blue-800 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
            :disabled="buscando"
            autofocus
          />
          <div v-if="buscando" class="absolute right-3 top-1/2 -translate-y-1/2">
            <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
        <button 
          @click="buscarProductoPorCodigo" 
          :disabled="!codigoEscaneado.trim() || buscando"
          class="px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold shadow-lg shadow-blue-500/20"
        >
          <Icon :icon="buscando ? 'mdi:loading' : 'mdi:magnify'" :class="buscando ? 'animate-spin' : ''" />
          {{ buscando ? 'Buscando...' : 'Buscar' }}
        </button>
        <button 
          v-if="productoEncontrado || busquedaFallida"
          @click="limpiarBusqueda"
          class="px-4 py-3 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
        >
          <Icon icon="mdi:close" class="text-xl" />
        </button>
      </div>

      <!-- Resultado de búsqueda -->
      <div v-if="productoEncontrado" class="mt-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
        <div class="flex items-start gap-4">
          <div class="w-20 h-20 rounded-lg overflow-hidden bg-white flex-shrink-0 border border-gray-200">
            <img 
              v-if="getImageUrl(productoEncontrado.imagen)" 
              :src="getImageUrl(productoEncontrado.imagen)" 
              :alt="productoEncontrado.nombre"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
              <Icon icon="mdi:package-variant" class="text-3xl" />
            </div>
          </div>
          <div class="flex-1">
            <div class="flex items-start justify-between">
              <div>
                <h4 class="font-bold text-gray-800 dark:text-white">{{ productoEncontrado.nombre }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  <span class="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">{{ productoEncontrado.codigo }}</span>
                  <span v-if="productoEncontrado.codigoBarras" class="ml-1 bg-blue-100 dark:bg-blue-900 px-2 py-0.5 rounded text-xs font-mono text-blue-700 dark:text-blue-300">
                    <Icon icon="mdi:barcode-scan" class="inline" /> {{ productoEncontrado.codigoBarras }}
                  </span>
                  <span class="mx-2">|</span>
                  {{ productoEncontrado.marca || 'Sin marca' }}
                  <span v-if="productoEncontrado.modelo">- {{ productoEncontrado.modelo }}</span>
                </p>
              </div>
              <span :class="['px-3 py-1 rounded-full text-xs font-bold', productoEncontrado.estado?.toLowerCase() === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700']">
                {{ productoEncontrado.estado }}
              </span>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
              <div>
                <span class="text-gray-500">Stock:</span>
                <span :class="['font-bold', getStockClass(productoEncontrado.stock, productoEncontrado.stock_minimo)]">
                  {{ productoEncontrado.stock || 0 }}
                </span>
              </div>
              <div>
                <span class="text-gray-500">Precio:</span>
                <span class="font-bold text-green-600">${{ formatNumber(productoEncontrado.precio_venta_usd, 2) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Categoría:</span>
                <span>{{ productoEncontrado.nombreCategoria || 'Sin cat.' }}</span>
              </div>
              <div>
                <span class="text-gray-500">Proveedor:</span>
                <span>{{ productoEncontrado.nombreProveedor || 'Sin prov.' }}</span>
              </div>
            </div>
            <div class="mt-2 flex gap-2">
              <button @click="openEditModal(productoEncontrado)" class="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600 transition-all flex items-center gap-1">
                <Icon icon="mdi:pencil" /> Editar
              </button>
              <button @click="openStockModal(productoEncontrado)" class="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs hover:bg-amber-600 transition-all flex items-center gap-1">
                <Icon icon="mdi:package-up" /> Ajustar Stock
              </button>
              <button @click="copiarProducto(productoEncontrado)" class="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-xs hover:bg-purple-600 transition-all flex items-center gap-1">
                <Icon icon="mdi:content-copy" /> Duplicar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Producto no encontrado -->
      <div v-else-if="busquedaFallida" class="mt-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 text-center">
        <Icon icon="mdi:alert-circle" class="text-red-500 text-2xl mb-1" />
        <p class="text-red-600 dark:text-red-400 font-medium">Producto no encontrado</p>
        <p class="text-xs text-gray-500 mt-1">El código "{{ codigoEscaneado }}" no existe en el inventario</p>
        <button @click="abrirCrearDesdeCodigoEscaneado" class="mt-2 px-4 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600 transition-all">
          <Icon icon="mdi:plus" /> Crear con este código
        </button>
      </div>
    </div>

    <!-- Filtros Compactos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-2 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
        <div class="md:col-span-4">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Buscar Producto</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Código, nombre, marca..." @input="handleSearch" class="w-full pl-11 pr-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" />
          </div>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Estado</label>
          <select v-model="filterStatus" @change="handleFilterChange" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="all" class="dark:bg-gray-800 dark:text-white">Todos</option>
            <option value="activos" class="dark:bg-gray-800 dark:text-white">Activos</option>
            <option value="inactivos" class="dark:bg-gray-800 dark:text-white">Inactivos</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Stock</label>
          <select v-model="filterStock" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="all" class="dark:bg-gray-800 dark:text-white">Cualquier Stock</option>
            <option value="con_stock" class="dark:bg-gray-800 dark:text-white">Con Stock</option>
            <option value="stock_cero" class="dark:bg-gray-800 dark:text-white">Sin Stock</option>
            <option value="stock_bajo" class="dark:bg-gray-800 dark:text-white">Stock Bajo</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Proveedor</label>
          <select v-model="filterProveedor" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="all" class="dark:bg-gray-800 dark:text-white">Todos</option>
            <option v-for="prov in proveedores" :key="prov.idProveedor" :value="prov.idProveedor" class="dark:bg-gray-800 dark:text-white">{{ prov.nombre }}</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Ordenar</label>
          <select v-model="sortBy" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="nombre" class="dark:bg-gray-800 dark:text-white">Nombre (A-Z)</option>
            <option value="precioVenta_desc" class="dark:bg-gray-800 dark:text-white">Precio Máximo</option>
            <option value="stock_desc" class="dark:bg-gray-800 dark:text-white">Mayor Stock</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tasas -->
    <div v-if="tasaUSDT && tasaBCV" class="grid grid-cols-2 gap-3 mb-4">
      <div class="bg-blue-600 text-white p-2 rounded-xl flex items-center justify-between shadow-sm border border-blue-500">
        <div class="flex items-center gap-2">
          <div class="p-1.5 bg-white/20 rounded-lg"><Icon icon="mdi:currency-usdt" class="text-lg" /></div>
          <div><p class="text-[10px] opacity-80 leading-tight">Tasa USDT</p><p class="text-sm font-bold leading-tight">{{ formatNumber(tasaUSDT?.precio_usdt_bs, 2) }} Bs</p></div>
        </div>
        <div class="text-right"><p class="text-[9px] opacity-70">{{ tasaUSDT?.fuente }}</p></div>
      </div>
      <div class="bg-amber-600 text-white p-2 rounded-xl flex items-center justify-between shadow-sm border border-amber-500">
        <div class="flex items-center gap-2">
          <div class="p-1.5 bg-white/20 rounded-lg"><Icon icon="mdi:bank" class="text-lg" /></div>
          <div><p class="text-[10px] opacity-80 leading-tight">Tasa BCV</p><p class="text-sm font-bold leading-tight">{{ formatNumber(tasaBCV?.precio_usd_bs, 2) }} Bs</p></div>
        </div>
        <div class="text-right"><p class="text-[9px] opacity-70">{{ tasaBCV?.fuente }}</p></div>
      </div>
    </div>

    <!-- Lista Productos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
        <div>
          <h2 class="text-base font-bold text-gray-800 dark:text-white leading-none mb-1">Productos</h2>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium leading-none">{{ filteredProductos.length }} productos registrados</p>
        </div>
        <div class="text-xs text-gray-400 font-medium"><Icon icon="mdi:calendar" class="inline mb-0.5" /> {{ formatTime(lastUpdate) }}</div>
      </div>

      <div v-if="loading" class="loading-spinner"><div class="spinner"></div><p>Cargando...</p></div>

      <div v-else-if="filteredProductos.length === 0" class="empty-state">
        <Icon icon="mdi:package-variant-off" />
        <h3>No hay productos</h3>
        <p>{{ searchQuery || filterStatus !== 'all' ? 'Cambia los filtros' : 'Registra el primero' }}</p>
        <button v-if="!searchQuery && filterStatus === 'all'" @click="openCreateModal">Registrar</button>
        <button v-else @click="clearFilters">Limpiar</button>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 p-3">
        <div v-for="producto in paginatedProductos" :key="producto.idProducto" class="producto-card">
          <div class="producto-imagen">
            <span @click="abrirModalToggleStatus(producto)" :class="['estado-badge', producto.estado?.toLowerCase() === 'activo' ? 'estado-activo' : 'estado-inactivo']">
              <Icon :icon="producto.estado?.toLowerCase() === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" />
              {{ producto.estado === 'Activo' ? 'Activo' : 'Inactivo' }}
            </span>
            <div class="imagen-contenedor">
              <img v-if="getImageUrl(producto.imagen)" :src="getImageUrl(producto.imagen)" :alt="producto.nombre" @error="handleImageError" />
              <Icon v-else icon="mdi:package-variant" />
            </div>
            <span :class="['stock-badge', getStockClass(producto.stock, producto.stock_minimo)]">Stock: {{ producto.stock || 0 }}</span>
          </div>
          <div class="producto-info">
            <div class="producto-codigo">
              <Icon icon="mdi:barcode" /> {{ producto.codigo }}
              <span v-if="producto.codigoBarras" class="ml-1 text-blue-600 dark:text-blue-400 text-[10px]">
                <Icon icon="mdi:barcode-scan" class="inline" /> {{ producto.codigoBarras }}
              </span>
              <span class="categoria-badge"><Icon icon="mdi:folder" /> {{ producto.nombreCategoria || 'Sin cat.' }}</span>
            </div>
            <h3>{{ producto.nombre }}</h3>
            <p>{{ producto.marca || 'Sin marca' }} - {{ producto.modelo || 'Sin modelo' }}</p>
            <div class="precios">
              <div><span>Compra:</span><span>{{ formatNumber(producto.precio_compra_usdt, 2) }}$ <small>{{ formatBolivares(producto.precio_compra_usdt, 'compra') }}</small></span></div>
              <div><span>Venta:</span><span class="precio-venta">{{ formatNumber(producto.precio_venta_usd, 2) }}$ <small>{{ formatBolivares(producto.precio_venta_usd, 'venta') }}</small></span></div>
              <div><span>Ganancia:</span><span :class="calcularDiferencia(producto.precio_compra_usdt, producto.precio_venta_usd) > 0 ? 'ganancia-positiva' : 'ganancia-negativa'">{{ formatNumber(calcularDiferencia(producto.precio_compra_usdt, producto.precio_venta_usd), 2) }}$</span></div>
              <div class="margen"><span>Margen:</span><span>{{ calcularPorcentajeGanancia(producto.precio_compra_usdt, producto.precio_venta_usd) }}%</span></div>
            </div>
            <div class="producto-detalles">
              <div><Icon icon="mdi:account" /> {{ producto.nombreProveedor || 'Sin proveedor' }}</div>
              <div><Icon icon="mdi:calendar" /> {{ formatDate(producto.fecha_creacion, false) }}</div>
            </div>
            <div class="producto-acciones">
              <button @click.stop="openEditModal(producto)" class="btn-editar" title="Editar"><Icon icon="mdi:pencil" /></button>
              <button @click.stop="openStockModal(producto)" class="btn-stock" title="Ajustar Stock"><Icon icon="mdi:package-up" /></button>
              <button @click.stop="abrirModalRecalcularIndividual(producto)" class="btn-calcular" title="Recalcular"><Icon icon="mdi:calculator" /></button>
              <button @click.stop="abrirModalEliminar(producto)" class="btn-eliminar" title="Eliminar"><Icon icon="mdi:delete" /></button>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación Premium -->
      <div v-if="filteredProductos.length > itemsPerPage" class="bg-gray-50/50 dark:bg-gray-800/50 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
          Mostrando {{ startItem }} - {{ Math.min(endItem, filteredProductos.length) }} <span class="mx-1 text-gray-300">|</span> Total: {{ filteredProductos.length }}
        </div>
        <div class="flex items-center gap-1">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-lg" />
          </button>
          
          <div class="flex items-center gap-1 mx-1">
            <button v-for="page in visiblePages" :key="page" @click="currentPage = page" :class="['w-8 h-8 rounded-lg text-xs font-bold transition-all flex items-center justify-center', currentPage === page ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>

          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL PRODUCTO (Rediseñado estilo Ventas) -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999]" @click.self="closeModal">
          
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden animate-zoomIn max-h-[95vh]">
            
            <!-- Header Fijo -->
            <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-white/20 rounded-xl">
                  <Icon :icon="editingProducto ? 'mdi:pencil-box' : 'mdi:package-variant-plus'" class="text-white text-xl" />
                </div>
                <div>
                  <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">
                    {{ editingProducto ? 'Editar Producto' : 'Nuevo Producto' }}
                  </h3>
                  <p class="text-[10px] text-blue-100 font-bold">
                    {{ editingProducto ? 'Modifica los datos del producto' : 'Registra un nuevo producto en el inventario' }}
                  </p>
                </div>
              </div>
              <button @click="closeModal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
                <Icon icon="mdi:close" class="text-white text-lg" />
              </button>
            </div>

            <!-- Formulario con scroll interno -->
            <form @submit.prevent="saveProducto" class="flex flex-col flex-1 min-h-0 overflow-hidden">
              
              <!-- Body scrolleable -->
              <div class="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar space-y-6">
                
                <!-- SECCIÓN 1 -->
                <div class="animate-fadeIn">
                  <div class="flex items-center gap-2 mb-3 text-blue-700 dark:text-blue-400">
                    <Icon icon="mdi:information-outline" class="text-lg" />
                    <h4 class="text-[11px] font-black uppercase tracking-widest">Información Básica</h4>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Código *</label>
                      <input v-model="form.codigo" type="text" required maxlength="50" placeholder="Ej: PROD-001" @blur="verificarCodigoUnico" class="modern-input" />
                      <p v-if="errors.codigo" class="text-[10px] text-red-500 mt-1">{{ errors.codigo }}</p>
                    </div>
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Código de Barras</label>
                      <input v-model="form.codigoBarras" type="text" maxlength="100" placeholder="Opcional" @blur="verificarCodigoBarrasUnico" class="modern-input" :class="{ 'border-green-500 bg-green-50 dark:bg-green-900/20': codigoDesdeLector }" />
                      <p v-if="errors.codigoBarras" class="text-[10px] text-red-500 mt-1">{{ errors.codigoBarras }}</p>
                      <p v-if="codigoDesdeLector" class="text-[10px] text-green-600 dark:text-green-400 mt-1"><Icon icon="mdi:check-circle" class="inline" /> Escaneado</p>
                    </div>
                    <div class="sm:col-span-2">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Nombre *</label>
                      <input v-model="form.nombre" type="text" required maxlength="100" placeholder="Ej: Monitor LED" class="modern-input" @blur="form.nombre = toTitleCase(form.nombre)" />
                    </div>
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Categoría *</label>
                      <select v-model="form.idCategoria" required class="modern-input">
                        <option value="">Seleccionar...</option>
                        <option v-for="categoria in categorias" :key="categoria.idCategoria || categoria.IdCategoria" :value="categoria.idCategoria || categoria.IdCategoria">{{ categoria.nombre || categoria.Nombre || 'Sin nombre' }}</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Proveedor *</label>
                      <select v-model="form.idProveedor" required class="modern-input">
                        <option value="">Seleccionar...</option>
                        <option v-for="proveedor in proveedores" :key="proveedor.idProveedor || proveedor.IdProveedor" :value="proveedor.idProveedor || proveedor.IdProveedor">{{ proveedor.nombre || proveedor.Nombre || 'Sin nombre' }}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <hr class="border-gray-100 dark:border-gray-700">

                <!-- SECCIÓN 2 -->
                <div class="animate-fadeIn">
                  <div class="flex items-center gap-2 mb-3 text-indigo-700 dark:text-indigo-400">
                    <Icon icon="mdi:details" class="text-lg" />
                    <h4 class="text-[11px] font-black uppercase tracking-widest">Detalles y Apariencia</h4>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div class="space-y-3">
                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Marca</label>
                          <input v-model="form.marca" type="text" maxlength="50" placeholder="Ej: Samsung" class="modern-input" @blur="form.marca = toTitleCase(form.marca)" />
                        </div>
                        <div>
                          <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Modelo</label>
                          <input v-model="form.modelo" type="text" maxlength="50" placeholder="Ej: S24F350" class="modern-input" />
                        </div>
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Descripción</label>
                        <textarea v-model="form.descripcion" rows="4" maxlength="500" placeholder="Breve descripción..." class="modern-input resize-none"></textarea>
                      </div>
                    </div>
                    
                    <!-- Subida de Imagen -->
                    <div class="flex flex-col">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Imagen del Producto</label>
                      <div class="flex-1 flex flex-col justify-center items-center p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800/50 transition-colors hover:border-blue-400">
                        <div class="w-full aspect-[4/3] max-h-[120px] rounded-lg overflow-hidden bg-white dark:bg-gray-700 flex items-center justify-center mb-3">
                          <img v-if="form.imagenPreview || getImageUrl(form.imagen)" :src="form.imagenPreview || getImageUrl(form.imagen)" @error="handleImageError" class="w-full h-full object-contain p-1" />
                          <div v-else class="text-gray-400 flex flex-col items-center">
                            <Icon icon="mdi:image-plus" class="text-3xl mb-1 opacity-50" />
                            <span class="text-[9px] font-bold uppercase">Sin Imagen</span>
                          </div>
                        </div>
                        <div class="flex gap-2 w-full">
                          <label class="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[10px] font-bold text-center cursor-pointer transition-colors uppercase">
                            <Icon icon="mdi:camera" class="inline mr-1 text-sm" />
                            {{ form.imagenPreview || form.imagen ? 'Cambiar' : 'Subir Foto' }}
                            <input type="file" accept="image/*" @change="onImageSelected" hidden />
                          </label>
                          <button v-if="form.imagenPreview || form.imagen" type="button" @click="eliminarImagen" class="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition-colors">
                            <Icon icon="mdi:trash-can-outline" class="text-sm" />
                          </button>
                        </div>
                        <!-- Barra de progreso de subida -->
                        <div v-if="imageUploading" class="w-full mt-2">
                          <div class="flex justify-between text-[9px] text-gray-500 mb-1">
                            <span>Subiendo...</span>
                            <span>{{ uploadProgress }}%</span>
                          </div>
                          <div class="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div class="h-full bg-blue-500 transition-all duration-300" :style="{ width: uploadProgress + '%' }"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <hr class="border-gray-100 dark:border-gray-700">

                <!-- SECCIÓN 3 -->
                <div class="animate-fadeIn">
                  <div class="flex items-center gap-2 mb-3 text-emerald-700 dark:text-emerald-400">
                    <Icon icon="mdi:cash-multiple" class="text-lg" />
                    <h4 class="text-[11px] font-black uppercase tracking-widest">Precios y Rentabilidad</h4>
                  </div>
                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div class="col-span-1">
                      <label class="block text-[10px] font-bold text-emerald-600 uppercase mb-1">Costo ($) *</label>
                      <input v-model.number="form.precioCompra" type="number" step="0.01" min="0.01" required placeholder="0.00" class="modern-input border-emerald-300 focus:border-emerald-500 font-bold" />
                    </div>
                    <div class="col-span-1">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Tipo Ganancia</label>
                      <select v-model="form.tipoGanancia" class="modern-input">
                        <option value="PORCENTAJE">Porcentaje (%)</option>
                        <option value="MONTO_FIJO">Monto Fijo ($)</option>
                      </select>
                    </div>
                    <div class="col-span-2 sm:col-span-1">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Valor Ganancia</label>
                      <input v-model.number="form.valorGanancia" type="number" step="0.01" min="0" :placeholder="form.tipoGanancia === 'PORCENTAJE' ? '30' : '15'" class="modern-input" />
                    </div>
                    <div class="col-span-2 sm:col-span-1">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Precio Especial ($)</label>
                      <input v-model.number="form.precioEspecialUsd" type="number" step="0.01" min="0" placeholder="0.00" class="modern-input" />
                    </div>
                    <div class="col-span-2 sm:col-span-4 mt-1">
                      <label class="block text-[10px] font-bold text-blue-600 uppercase mb-1">Precio de Venta Sugerido (Calculado)</label>
                      <div class="relative">
                        <input :value="form.precioVentaCalculado" type="text" readonly class="w-full px-3 py-2 bg-blue-50 dark:bg-blue-900/20 font-black text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg text-sm" />
                        <span class="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-500 bg-white dark:bg-gray-800 px-2 py-0.5 rounded border border-blue-200">USD</span>
                      </div>
                    </div>
                  </div>
                </div>

                <hr class="border-gray-100 dark:border-gray-700">

                <!-- SECCIÓN 4 -->
                <div class="animate-fadeIn">
                  <div class="flex items-center gap-2 mb-3 text-orange-700 dark:text-orange-400">
                    <Icon icon="mdi:store-24-hour" class="text-lg" />
                    <h4 class="text-[11px] font-black uppercase tracking-widest">Inventario y Garantía</h4>
                  </div>
                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Stock Actual</label>
                      <input v-model.number="form.stock" type="number" min="0" placeholder="0" class="modern-input" />
                    </div>
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Stock Mín.</label>
                      <input v-model.number="form.stockMinimo" type="number" min="0" placeholder="5" class="modern-input text-red-500" />
                    </div>
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Stock Máx.</label>
                      <input v-model.number="form.stockMaximo" type="number" min="0" placeholder="Opcional" class="modern-input" />
                    </div>
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Meses Garantía</label>
                      <input v-model.number="form.garantiaMeses" type="number" min="0" placeholder="0" class="modern-input" />
                    </div>
                  </div>
                  
                  <div class="flex items-center justify-between mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-600">
                    <label class="text-[10px] font-black text-gray-500 uppercase tracking-widest">Estado del Producto:</label>
                    <div class="flex bg-white dark:bg-gray-800 p-1 rounded-lg shadow-inner gap-1 border border-gray-200 dark:border-gray-600">
                      <button type="button" @click="form.estado = 'Activo'" :class="['px-4 py-1.5 rounded-md text-[10px] font-black uppercase transition-all', form.estado === 'Activo' ? 'bg-green-500 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700']">Activo</button>
                      <button type="button" @click="form.estado = 'Inactivo'" :class="['px-4 py-1.5 rounded-md text-[10px] font-black uppercase transition-all', form.estado === 'Inactivo' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700']">Inactivo</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer Fijo -->
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row justify-end gap-2 flex-shrink-0">
                <button type="button" @click="closeModal" class="w-full sm:w-auto px-4 sm:px-6 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase hover:text-gray-600 transition-colors">Cancelar</button>
                <button type="submit" :disabled="saving || !isFormValid" class="w-full sm:w-auto px-6 sm:px-8 py-2 bg-blue-600 text-white rounded-xl text-[10px] sm:text-xs font-black uppercase shadow-md hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                  <Icon v-if="saving" icon="mdi:loading" class="animate-spin text-sm" />
                  {{ saving ? 'Guardando...' : (editingProducto ? 'Actualizar' : 'Crear Producto') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- MODAL STOCK -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showStockModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9998]" @click.self="closeStockModal">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden animate-zoomIn">
            <div class="bg-gradient-to-r from-amber-500 to-orange-500 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-white/20 rounded-xl"><Icon icon="mdi:package-up" class="text-white text-lg" /></div>
                <div>
                  <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">Ajustar Stock</h3>
                  <p class="text-[10px] text-amber-100 font-bold truncate max-w-[200px]">{{ stockProducto?.nombre }}</p>
                </div>
              </div>
              <button @click="closeStockModal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all"><Icon icon="mdi:close" class="text-white text-lg" /></button>
            </div>
            <form @submit.prevent="saveStock" class="p-4 sm:p-6">
              <div class="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-xl border border-gray-100 dark:border-gray-700 text-center mb-4">
                <span class="text-[10px] font-bold text-gray-500 uppercase block mb-1">Stock actual</span>
                <span class="text-2xl font-black text-amber-600">{{ stockProducto?.stock || 0 }}</span>
              </div>
              <div class="space-y-3">
                <div>
                  <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Operación</label>
                  <select v-model="stockForm.operacion" class="modern-input"><option value="incrementar">Incrementar (+)</option><option value="disminuir">Disminuir (-)</option></select>
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Cantidad</label>
                  <input v-model.number="stockForm.cantidad" type="number" min="0" required placeholder="0" class="modern-input text-lg font-bold text-center" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-500 uppercase mb-1">Motivo</label>
                  <input v-model="stockForm.motivo" type="text" placeholder="Opcional" class="modern-input" />
                </div>
              </div>
              <div class="flex gap-2 mt-6">
                <button type="button" @click="closeStockModal" class="flex-1 px-4 py-2 text-[10px] font-black text-gray-400 uppercase border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
                <button type="submit" :disabled="savingStock" class="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg text-[10px] font-black uppercase shadow-md hover:bg-amber-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                  <Icon v-if="savingStock" icon="mdi:loading" class="animate-spin" />
                  {{ savingStock ? 'Aplicando...' : 'Aplicar' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[10000]" @click.self="cancelarConfirmacion">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden animate-zoomIn" :class="confirmModalColor">
            <div class="confirm-header px-4 py-3 flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl"><Icon :icon="confirmModalIcon" class="text-white text-lg" /></div>
              <div>
                <h3 class="text-sm font-bold text-white uppercase tracking-wider">{{ confirmModalTitulo }}</h3>
                <p class="text-[10px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
            <div class="p-4 sm:p-6">
              <p class="text-sm text-gray-800 dark:text-gray-200 text-center font-medium">{{ confirmModalMensaje }}</p>
              <p v-if="confirmModalDetalle" class="text-[10px] text-gray-500 text-center mt-2">{{ confirmModalDetalle }}</p>
            </div>
            <div class="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
              <button @click="cancelarConfirmacion" class="px-4 py-1.5 text-[10px] font-black text-gray-400 uppercase hover:text-gray-600 transition-colors">Cancelar</button>
              <button @click="ejecutarAccionConfirmada" :class="['px-6 py-1.5 rounded-lg text-[10px] font-black text-white uppercase shadow-md transition-all', confirmModalButtonColor]">{{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";
import {
  obtenerProductos, 
  crearProducto, 
  actualizarProducto, 
  eliminarProducto,
  cambiarEstadoProducto, 
  actualizarStock, 
  subirImagenProducto, 
  eliminarImagenProducto,
  verificarCodigo,
  verificarCodigoBarras,
  ordenarProductos, 
  formatearMonto, 
  obtenerUrlImagen,
  obtenerProductoPorCodigo,
  obtenerProductoPorCodigoBarras
} from '@/api/productos.js';
import { obtenerCategorias } from '@/api/categorias.js';
import { obtenerProveedores } from '@/api/proveedores.js';
import { obtenerTasaCompraActual } from '@/api/tasaCompra.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { filtrarProductosParaReporte, cargarLogo } from '@/utils/reporteProductosGenerator.js';
import { toTitleCase } from '@/utils/stringUtils.js';
import { notificarProducto } from '@/utils/notificacionesHelper';

export default {
  name: 'ProductosList',
  components: { Icon },
  setup() { const toast = useToast(); return { toast }; },
  data() {
    return {
      serverConfig: { host: '192.168.9.195', port: '5090', localhost: 'localhost' },
      productos: [], categorias: [], proveedores: [],
      tasaUSDT: null, tasaBCV: null, tasaInterval: null,
      loading: false, saving: false, savingStock: false,
      showModal: false, showStockModal: false,
      searchQuery: '', filterStatus: 'all', filterStock: 'all', filterProveedor: 'all', sortBy: 'nombre',
      currentPage: 1, itemsPerPage: 6,
      // Lector de código
      codigoEscaneado: '',
      productoEncontrado: null,
      busquedaFallida: false,
      buscando: false,
      codigoDesdeLector: false,
      lectorHandler: null,
      form: {
        idProducto: null, 
        codigo: '', 
        codigoBarras: '',
        nombre: '', 
        descripcion: '', 
        marca: '', 
        modelo: '',
        precioCompra: '', 
        precioVentaCalculado: '0.00', 
        precioVentaEnviar: null, 
        precioEspecialUsd: null,
        stock: 0, 
        stockMinimo: 5, 
        stockMaximo: null, 
        garantiaMeses: 0, 
        garantiaDescripcion: '',
        idCategoria: '', 
        idProveedor: '', 
        imagen: '', 
        imagenPreview: '', 
        estado: 'Activo',
        tipoGanancia: 'PORCENTAJE', 
        valorGanancia: 30, 
        ubicacion: '', 
        imagenFile: null
      },
      stockForm: { operacion: 'incrementar', cantidad: 0, motivo: '' },
      stockProducto: null, errors: {}, editingProducto: null, lastUpdate: new Date(),
      imageUploading: false, uploadProgress: 0,
      showConfirmModal: false, confirmModalTipo: '', confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '', confirmModalColor: '',
      confirmModalButtonColor: '', confirmModalBotonTexto: '', confirmModalProducto: null,
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    serverIp() { return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? this.serverConfig.localhost : this.serverConfig.host; },
    serverPort() { return this.serverConfig.port; },
    imageBaseUrl() { return `http://${this.serverIp}:${this.serverPort}/imagenes/producto`; },
    filteredProductos() {
      let filtered = [...this.productos];
      if (this.searchQuery.trim()) {
        const normalize = (str) => (str || '').toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const terms = normalize(this.searchQuery).split(/\s+/).filter(Boolean);
        
        filtered = filtered.filter(p => {
          const searchableText = normalize([
            p.codigo, p.Codigo,
            p.codigoBarras, p.CodigoBarras,
            p.nombre, p.Nombre,
            p.descripcion, p.Descripcion,
            p.marca, p.Marca,
            p.modelo, p.Modelo,
            p.nombreCategoria, p.NombreCategoria,
            p.nombreProveedor, p.NombreProveedor
          ].join(' '));
          
          return terms.every(term => searchableText.includes(term));
        });
      }
      if (this.filterStatus === 'activos') filtered = filtered.filter(p => p.estado?.toLowerCase() === 'activo');
      else if (this.filterStatus === 'inactivos') filtered = filtered.filter(p => p.estado?.toLowerCase() === 'inactivo');
      if (this.filterStock !== 'all') filtered = filtrarProductosParaReporte(filtered, this.filterStock);
      if (this.filterProveedor !== 'all') filtered = filtered.filter(p => p.idProveedor === parseInt(this.filterProveedor));
      return ordenarProductos(filtered, this.sortBy);
    },
    paginatedProductos() { const start = (this.currentPage - 1) * this.itemsPerPage; return this.filteredProductos.slice(start, start + this.itemsPerPage); },
    totalPages() { return Math.ceil(this.filteredProductos.length / this.itemsPerPage); },
    visiblePages() { const pages = []; const maxVisible = 5; let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2)); let end = Math.min(this.totalPages, start + maxVisible - 1); if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1); for (let i = start; i <= end; i++) pages.push(i); return pages; },
    startItem() { return (this.currentPage - 1) * this.itemsPerPage + 1; },
    endItem() { return Math.min(this.currentPage * this.itemsPerPage, this.filteredProductos.length); },
    isFormValid() { return this.form.codigo.trim() && this.form.nombre.trim() && this.form.precioCompra && this.form.precioCompra > 0 && this.form.idCategoria && this.form.idProveedor && this.tasaUSDT && this.tasaBCV; },
  },
  watch: {
    'form.precioCompra'() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    'form.tipoGanancia'() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    'form.valorGanancia'() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    tasaUSDT() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    tasaBCV() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    filterStatus() { this.currentPage = 1; },
    filterStock() { this.currentPage = 1; },
    filterProveedor() { this.currentPage = 1; },
    searchQuery() { this.currentPage = 1; },
    sortBy() { this.currentPage = 1; },
  },
  async mounted() { 
    await cargarLogo(); 
    await this.cargarTasasCompletas(); 
    await this.loadData(); 
    this.tasaInterval = setInterval(() => this.cargarTasasCompletas(), 5 * 60 * 1000);
    this.setupLectorAutomatico();
    this.$nextTick(() => {
      this.$refs.lectorInput?.focus();
    });
  },
  beforeUnmount() { 
    if (this.tasaInterval) clearInterval(this.tasaInterval);
    if (this.lectorHandler) {
      document.removeEventListener('keydown', this.lectorHandler);
      this.lectorHandler = null;
    }
    if (this.handleSearch.cancel) this.handleSearch.cancel();
  },
  methods: {
    toTitleCase,
    // ============ LECTOR DE CÓDIGO ============
    setupLectorAutomatico() {
      let codigoTemporal = '';
      let ultimoTiempo = 0;
      const TIEMPO_LECTOR = 50;
      const TIEMPO_RESET = 200;

      this.lectorHandler = (e) => {
        const tag = document.activeElement?.tagName;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) && 
            document.activeElement !== this.$refs.lectorInput) {
          return;
        }

        const ahora = Date.now();
        const diferencia = ahora - ultimoTiempo;
        ultimoTiempo = ahora;

        if (['Shift','Control','Alt','CapsLock','Tab','Escape'].includes(e.key)) return;

        if (diferencia < TIEMPO_LECTOR && e.key !== 'Enter') {
          codigoTemporal += e.key;
          e.preventDefault();
        }

        if (e.key === 'Enter' && codigoTemporal.length > 2) {
          e.preventDefault();
          this.codigoEscaneado = codigoTemporal;
          this.buscarProductoPorCodigo();
          codigoTemporal = '';
        }

        if (diferencia > TIEMPO_RESET && e.key !== 'Enter') {
          codigoTemporal = '';
        }
      };

      document.addEventListener('keydown', this.lectorHandler);
    },
    async buscarProductoPorCodigo() {
      const codigo = this.codigoEscaneado.trim();
      if (!codigo) {
        this.toast.warning('Ingresa o escanea un código');
        return;
      }

      let producto = this.productos.find(p => {
        const codigoEsc = codigo.toLowerCase();
        return p.codigo?.toLowerCase() === codigoEsc ||
               p.codigoBarras?.toLowerCase() === codigoEsc;
      });

      if (!producto) {
        this.buscando = true;
        try {
          let resultado = await obtenerProductoPorCodigo(codigo);
          
          if (!resultado.success || !resultado.data) {
            resultado = await obtenerProductoPorCodigoBarras(codigo);
          }
          
          if (resultado.success && resultado.data) {
            producto = resultado.data;
            const existe = this.productos.some(p => p.idProducto === producto.idProducto);
            if (!existe) {
              this.productos.push(producto);
            }
          }
        } catch (error) {
          console.error('Error buscando producto:', error);
          this.toast.error('Error al buscar el producto');
          this.buscando = false;
          return;
        }
        this.buscando = false;
      }

      if (producto) {
        this.productoEncontrado = producto;
        this.busquedaFallida = false;
        this.codigoEscaneado = '';
        this.$refs.lectorInput?.focus();
        this.toast.success(`Producto encontrado: ${producto.nombre}`);
      } else {
        this.productoEncontrado = null;
        this.busquedaFallida = true;
        this.toast.error('Producto no encontrado');
      }
    },
    limpiarBusqueda() {
      this.codigoEscaneado = '';
      this.productoEncontrado = null;
      this.busquedaFallida = false;
      this.buscando = false;
      this.codigoDesdeLector = false;
      this.$refs.lectorInput?.focus();
    },
    abrirCrearDesdeCodigoEscaneado() {
      const codigoEscaneado = this.codigoEscaneado.trim();
      this.openCreateModal();
      
      if (codigoEscaneado) {
        this.form.codigoBarras = codigoEscaneado;
        this.codigoDesdeLector = true;
        this.toast.info('Código de barras cargado desde el escáner');
      }
      
      this.limpiarBusqueda();
    },
    copiarProducto(producto) {
      this.openCreateModal();
      this.form.codigo = `${producto.codigo}-COPIA`;
      this.form.codigoBarras = producto.codigoBarras || '';
      this.form.nombre = `${producto.nombre} (Copia)`;
      this.form.marca = producto.marca;
      this.form.modelo = producto.modelo;
      this.form.descripcion = producto.descripcion;
      this.form.precioCompra = producto.precio_compra_usdt;
      this.form.idCategoria = producto.idCategoria;
      this.form.idProveedor = producto.idProveedor;
      this.form.tipoGanancia = producto.tipo_ganancia || 'PORCENTAJE';
      this.form.valorGanancia = producto.valor_ganancia || 30;
      this.codigoDesdeLector = false;
      this.toast.success('Producto copiado, edita y guarda');
    },

    // ============ CÁLCULO DE PRECIOS ============
    calcularPrecioVentaBackend() {
      if (!this.tasaUSDT || !this.tasaBCV || !this.form.precioCompra || this.form.precioCompra <= 0) { 
        this.form.precioVentaCalculado = '0.00'; 
        this.form.precioVentaEnviar = null; 
        return; 
      }
      
      const costoRealUSD = parseFloat(this.form.precioCompra);
      
      let precioVentaUSD;
      if (this.form.tipoGanancia === 'PORCENTAJE') { 
        precioVentaUSD = costoRealUSD * (1 + ((this.form.valorGanancia || 30) / 100)); 
      } else { 
        precioVentaUSD = costoRealUSD + (this.form.valorGanancia || 15); 
      }
      
      const precioVentaRedondeado = Math.round(precioVentaUSD * 100) / 100;
      this.form.precioVentaCalculado = precioVentaRedondeado.toFixed(2);
      this.form.precioVentaEnviar = precioVentaRedondeado;
      return precioVentaRedondeado;
    },

    calcularDiferencia(precioCompra, precioVenta) { 
      return parseFloat(precioVenta || 0) - parseFloat(precioCompra || 0); 
    },
    calcularPorcentajeGanancia(precioCompra, precioVenta) { 
      const ganancia = this.calcularDiferencia(precioCompra, precioVenta); 
      if (!ganancia || ganancia <= 0 || !precioCompra || precioCompra <= 0) return '0'; 
      return ((ganancia / precioCompra) * 100).toFixed(2); 
    },
    formatBolivares(montoUSD, tipo = 'venta') { 
      if (!montoUSD || isNaN(montoUSD)) return '0 Bs.'; 
      let bolivares = 0; 
      if (tipo === 'compra' && this.tasaUSDT) bolivares = parseFloat(montoUSD) * (this.tasaUSDT?.precio_usdt_bs || 1); 
      else if (tipo === 'venta' && this.tasaBCV) bolivares = parseFloat(montoUSD) * (this.tasaBCV?.precio_usd_bs || 1); 
      else bolivares = parseFloat(montoUSD) * (this.tasaUSDT?.precio_usdt_bs || this.tasaBCV?.precio_usd_bs || 1); 
      return this.formatNumber(bolivares, 0) + ' Bs.'; 
    },
    formatNumber(numero, decimales = 2) { return formatearMonto(numero, decimales); },
    formatDate(fechaISO, completo = true) { 
      if (!fechaISO) return ''; 
      try { 
        const fecha = new Date(fechaISO); 
        const opciones = { year: 'numeric', month: '2-digit', day: '2-digit' }; 
        if (completo) opciones.hour = '2-digit', opciones.minute = '2-digit'; 
        return fecha.toLocaleDateString('es-VE', opciones); 
      } catch (error) { return fechaISO; } 
    },
    formatTime(fechaISO) { 
      if (!fechaISO) return ''; 
      try { 
        const fecha = new Date(fechaISO); 
        return fecha.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: false }); 
      } catch (error) { return ''; } 
    },
    getStockClass(stock, stockMinimo) { 
      const stockNum = Number(stock) || 0; 
      const minNum = Number(stockMinimo) || 0; 
      if (stockNum <= 0) return 'stock-cero'; 
      if (minNum > 0 && stockNum <= minNum) return 'stock-bajo'; 
      return 'stock-normal'; 
    },
    async onImageSelected(event) { 
      const file = event.target.files[0]; 
      if (!file) return; 
      const tipos = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']; 
      if (!tipos.includes(file.type)) { this.toast.error('Formato no válido'); return; } 
      if (file.size > 5 * 1024 * 1024) { this.toast.error('Máx. 5MB'); return; } 
      this.form.imagenFile = file; 
      const reader = new FileReader(); 
      reader.onload = (e) => { this.form.imagenPreview = e.target.result; }; 
      reader.readAsDataURL(file); 
    },
    async subirImagenDespuesDeGuardar(productoId) { 
      if (!this.form.imagenFile) return; 
      this.imageUploading = true; 
      this.uploadProgress = 0; 
      try { 
        const interval = setInterval(() => { if (this.uploadProgress < 90) this.uploadProgress += 10; }, 100); 
        const res = await subirImagenProducto(this.form.imagenFile, productoId); 
        clearInterval(interval); 
        this.uploadProgress = 100; 
        if (res.success) { 
          this.form.imagen = res.data.nombreArchivo; 
          this.toast.success('Imagen subida'); 
          await actualizarProducto(productoId, { imagen: res.data.nombreArchivo }); 
        } else { this.toast.error(res.error || 'Error'); } 
      } catch (error) { this.toast.error('Error'); } 
      finally { this.imageUploading = false; this.uploadProgress = 0; this.form.imagenFile = null; } 
    },
    async eliminarImagen() { 
      if (!confirm('¿Eliminar esta imagen?')) return; 
      this.imageUploading = true; 
      try { 
        if (this.editingProducto?.idProducto) { 
          const res = await eliminarImagenProducto('producto', this.editingProducto.idProducto); 
          if (res.success) { 
            this.form.imagen = ''; 
            this.form.imagenPreview = ''; 
            this.toast.success('Imagen eliminada'); 
          } else { this.toast.error(res.error || 'Error'); } 
        } else { 
          this.form.imagen = ''; 
          this.form.imagenPreview = ''; 
          this.form.imagenFile = null; 
        } 
      } catch (error) { this.toast.error('Error'); } 
      finally { this.imageUploading = false; } 
    },
    getImageUrl(imagen) { 
      return obtenerUrlImagen(imagen);
    },
    handleImageError(event) { event.target.style.display = 'none'; },
    abrirModalConfirmacion(config) { 
      this.confirmModalTipo = config.tipo; 
      this.confirmModalTitulo = config.titulo; 
      this.confirmModalSubtitulo = config.subtitulo || ''; 
      this.confirmModalMensaje = config.mensaje; 
      this.confirmModalDetalle = config.detalle || ''; 
      this.confirmModalIcon = config.icon || 'mdi:help-circle'; 
      this.confirmModalColor = config.color || ''; 
      this.confirmModalButtonColor = config.buttonColor || ''; 
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar'; 
      this.confirmModalProducto = config.producto || null; 
      this.confirmModalAccion = config.accion; 
      this.confirmModalDatos = config.datos || null; 
      this.showConfirmModal = true; 
    },
    cancelarConfirmacion() { 
      this.showConfirmModal = false; 
      this.confirmModalCargando = false; 
      this.confirmModalProducto = null; 
      this.confirmModalAccion = null; 
      this.confirmModalDatos = null; 
    },
    async ejecutarAccionConfirmada() { 
      if (this.confirmModalAccion) { 
        this.confirmModalCargando = true; 
        await this.confirmModalAccion(this.confirmModalDatos); 
        this.confirmModalCargando = false; 
        this.cancelarConfirmacion(); 
      } 
    },
    abrirModalEliminar(producto) { 
      this.abrirModalConfirmacion({ 
        tipo: 'eliminar', titulo: 'Eliminar', subtitulo: 'Esta acción no se puede deshacer', 
        mensaje: `¿Eliminar "${producto.nombre}"?`, detalle: 'Eliminación permanente.', 
        icon: 'mdi:alert-circle', color: 'confirm-danger', buttonColor: 'bg-red-600 hover:bg-red-700', 
        botonTexto: 'Eliminar', producto: producto, accion: () => this.deleteProducto(producto.idProducto) 
      }); 
    },
    abrirModalToggleStatus(producto) { 
      const esActivo = producto.estado?.toLowerCase() === 'activo'; 
      this.abrirModalConfirmacion({ 
        tipo: 'toggle-status', titulo: esActivo ? 'Desactivar' : 'Activar', subtitulo: 'Cambiar estado', 
        mensaje: `¿${esActivo ? 'desactivar' : 'activar'} "${producto.nombre}"?`, 
        detalle: `Pasará a "${esActivo ? 'Inactivo' : 'Activo'}"`, 
        icon: esActivo ? 'mdi:close-circle' : 'mdi:check-circle', 
        color: esActivo ? 'confirm-warning' : 'confirm-success', 
        buttonColor: esActivo ? 'bg-orange-500 hover:bg-orange-600' : 'bg-emerald-500 hover:bg-emerald-600', 
        botonTexto: esActivo ? 'Desactivar' : 'Activar', producto: producto, accion: () => this.toggleStatus(producto) 
      }); 
    },
    abrirModalRecalcularMasivo() { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.toast.error('No hay tasas'); return; } 
      this.abrirModalConfirmacion({ 
        tipo: 'recalcular-masivo', titulo: 'Recalcular Precios', subtitulo: 'Actualizar todos', 
        mensaje: `¿Recalcular ${this.filteredProductos.length} productos?`, 
        detalle: 'Se usarán tasas actuales.', icon: 'mdi:calculator', color: 'confirm-amber', 
        buttonColor: 'bg-amber-500 hover:bg-amber-600', botonTexto: 'Recalcular', accion: this.recalcularPreciosMasivos 
      }); 
    },
    abrirModalRecalcularIndividual(producto) { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.toast.error('No hay tasas'); return; } 
      this.abrirModalConfirmacion({ 
        tipo: 'recalcular-individual', titulo: 'Recalcular Precio', subtitulo: 'Actualizar precio', 
        mensaje: `¿Recalcular "${producto.nombre}"?`, detalle: 'Se usará tasa actual.', 
        icon: 'mdi:calculator', color: 'confirm-blue', buttonColor: 'bg-blue-600 hover:bg-blue-700', 
        botonTexto: 'Recalcular', producto: producto, accion: () => this.recalcularPrecioIndividual(producto) 
      }); 
    },
    mostrarExito(mensaje) { this.toast.success(mensaje, { timeout: 3000 }); },
    mostrarError(mensaje) { this.toast.error(mensaje, { timeout: 5000 }); },
    mostrarAdvertencia(mensaje) { this.toast.warning(mensaje, { timeout: 4000 }); },
    async cargarTasasCompletas() { 
      try { 
        const compra = await obtenerTasaCompraActual(); 
        if (compra.success) this.tasaUSDT = compra.data; 
        const venta = await obtenerTasaVentaActual(); 
        if (venta.success) this.tasaBCV = venta.data; 
        if (this.showModal) this.calcularPrecioVentaBackend(); 
      } catch (error) { this.mostrarError('Error al cargar tasas'); } 
    },
    async loadData() { 
      this.loading = true; 
      try { 
        const [prod, cat, prov] = await Promise.all([obtenerProductos(this.filterStatus), obtenerCategorias('activas'), obtenerProveedores('activos')]); 
        if (prod.success) { this.productos = prod.data || []; this.lastUpdate = new Date(); } 
        if (cat.success) this.categorias = cat.data || []; 
        if (prov.success) this.proveedores = prov.data || []; 
      } catch (error) { this.mostrarError('Error al cargar datos'); } 
      finally { this.loading = false; } 
    },
    handleFilterChange() { this.currentPage = 1; this.loadData(); },
    handleSearch: debounce(function() { this.currentPage = 1; }, 300),
    openCreateModal() { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      this.editingProducto = null; 
      this.resetForm(); 
      this.errors = {}; 
      this.codigoDesdeLector = false;
      this.showModal = true; 
      this.$nextTick(() => { if (this.form.precioCompra) this.calcularPrecioVentaBackend(); }); 
    },
    openEditModal(producto) { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      this.editingProducto = producto; 
      this.form = { 
        idProducto: producto.idProducto, 
        codigo: producto.codigo || '', 
        codigoBarras: producto.codigoBarras || '',
        nombre: producto.nombre || '', 
        descripcion: producto.descripcion || '', 
        marca: producto.marca || '', 
        modelo: producto.modelo || '', 
        precioCompra: producto.precio_compra_usdt || '', 
        precioVentaCalculado: producto.precio_venta_usd ? parseFloat(producto.precio_venta_usd).toFixed(2) : '0.00', 
        precioVentaEnviar: producto.precio_venta_usd || null, 
        precioEspecialUsd: producto.precio_especial_usd || null, 
        stock: producto.stock || 0, 
        stockMinimo: producto.stock_minimo || 5, 
        stockMaximo: producto.stockMaximo || null, 
        garantiaMeses: producto.garantia_meses || 0, 
        garantiaDescripcion: producto.garantia_descripcion || '', 
        idCategoria: producto.idCategoria || '', 
        idProveedor: producto.idProveedor || '', 
        imagen: producto.imagen || '', 
        imagenPreview: '', 
        estado: producto.estado || 'Activo', 
        tipoGanancia: producto.tipo_ganancia || 'PORCENTAJE', 
        valorGanancia: producto.valor_ganancia || 30, 
        ubicacion: producto.ubicacion || '', 
        imagenFile: null 
      }; 
      this.errors = {}; 
      this.codigoDesdeLector = false;
      this.showModal = true; 
      this.$nextTick(() => { this.calcularPrecioVentaBackend(); }); 
    },
    openStockModal(producto) { 
      this.stockProducto = producto; 
      this.stockForm = { operacion: 'incrementar', cantidad: 0, motivo: '' }; 
      this.showStockModal = true; 
    },
    closeModal() { this.showModal = false; this.resetForm(); this.errors = {}; this.codigoDesdeLector = false; },
    closeStockModal() { this.showStockModal = false; this.stockProducto = null; this.stockForm = { operacion: 'incrementar', cantidad: 0, motivo: '' }; },
    resetForm() { 
      if (this.form.imagenPreview) URL.revokeObjectURL(this.form.imagenPreview); 
      this.form = { 
        idProducto: null, 
        codigo: '', 
        codigoBarras: '',
        nombre: '', 
        descripcion: '', 
        marca: '', 
        modelo: '', 
        precioCompra: '', 
        precioVentaCalculado: '0.00', 
        precioVentaEnviar: null, 
        precioEspecialUsd: null, 
        stock: 0, 
        stockMinimo: 5, 
        stockMaximo: null, 
        garantiaMeses: 0, 
        garantiaDescripcion: '', 
        idCategoria: '', 
        idProveedor: '', 
        imagen: '', 
        imagenPreview: '', 
        estado: 'Activo', 
        tipoGanancia: 'PORCENTAJE', 
        valorGanancia: 30, 
        ubicacion: '', 
        imagenFile: null 
      }; 
      this.codigoDesdeLector = false;
      this.editingProducto = null; 
    },
    async verificarCodigoUnico() { 
      if (!this.form.codigo.trim() || this.editingProducto?.codigo === this.form.codigo) return; 
      try { 
        const res = await verificarCodigo(this.form.codigo.trim(), this.editingProducto?.idProducto); 
        if (res.success && res.existe) this.errors.codigo = 'El código ya existe'; 
        else delete this.errors.codigo; 
      } catch (error) { } 
    },
    async verificarCodigoBarrasUnico() { 
      if (!this.form.codigoBarras?.trim()) {
        delete this.errors.codigoBarras;
        return;
      }
      if (this.editingProducto?.codigoBarras === this.form.codigoBarras) {
        delete this.errors.codigoBarras;
        return;
      }
      try { 
        const res = await verificarCodigoBarras(this.form.codigoBarras.trim(), this.editingProducto?.idProducto); 
        if (res.success && res.existe) this.errors.codigoBarras = 'El código de barras ya existe'; 
        else delete this.errors.codigoBarras; 
      } catch (error) { 
        console.error('Error verificando código de barras:', error);
      } 
    },
    validateForm() { 
      this.errors = {}; 
      if (!this.form.codigo.trim()) this.errors.codigo = 'Código requerido'; 
      if (!this.form.nombre.trim()) this.errors.nombre = 'Nombre requerido'; 
      if (!this.form.precioCompra || parseFloat(this.form.precioCompra) <= 0) this.errors.precioCompra = 'Precio > 0'; 
      if (!this.form.idCategoria) this.errors.idCategoria = 'Categoría requerida'; 
      if (!this.form.idProveedor) this.errors.idProveedor = 'Proveedor requerido'; 
      return Object.keys(this.errors).length === 0; 
    },

    async saveProducto() { 
      if (!this.validateForm()) return; 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      this.saving = true; 
      try { 
        const precioVenta = this.calcularPrecioVentaBackend(); 
        if (!precioVenta || precioVenta <= 0) throw new Error('Error calculando precio'); 

        const data = { 
          codigo: this.form.codigo.trim(), 
          codigoBarras: this.form.codigoBarras?.trim() || null,
          nombre: this.form.nombre.trim(), 
          descripcion: this.form.descripcion?.trim() || null, 
          marca: this.form.marca?.trim() || null, 
          modelo: this.form.modelo?.trim() || null, 
          precio_compra_usdt: parseFloat(this.form.precioCompra), 
          tipo_ganancia: this.form.tipoGanancia, 
          valor_ganancia: parseFloat(this.form.valorGanancia || (this.form.tipoGanancia === 'PORCENTAJE' ? 30 : 15)), 
          precio_venta_usd: precioVenta, 
          precio_venta_bs: precioVenta * (this.tasaBCV?.precio_usd_bs || 1), 
          precio_especial_usd: this.form.precioEspecialUsd ? parseFloat(this.form.precioEspecialUsd) : null, 
          stock: this.form.stock || 0, 
          stock_minimo: this.form.stockMinimo || 5, 
          stockMaximo: this.form.stockMaximo || null, 
          garantia_meses: this.form.garantiaMeses || 0, 
          garantia_descripcion: this.form.garantiaDescripcion || null, 
          imagen: this.editingProducto ? (this.form.imagen || null) : null, 
          ubicacion: this.form.ubicacion || null, 
          estado: this.form.estado, 
          idCategoria: parseInt(this.form.idCategoria), 
          idProveedor: parseInt(this.form.idProveedor) 
        }; 

        let result, productoId; 
        if (this.editingProducto) { 
          result = await actualizarProducto(this.editingProducto.idProducto, data); 
          productoId = this.editingProducto.idProducto; 
        } else { 
          result = await crearProducto(data); 
          productoId = result.data?.idProducto; 
        } 

        if (!result.success) throw new Error(result.error || 'Error al guardar');

        if (this.form.imagenFile && productoId) {
          await this.subirImagenDespuesDeGuardar(productoId);
        }

        const esEdicion = !!this.editingProducto;
        const nombreGuardado = this.form.nombre;
        const codigoGuardado = this.form.codigo;
        const precioGuardado = parseFloat(this.form.precioCompra);
        
        this.closeModal(); 
        this.codigoDesdeLector = false;
        this.mostrarExito(esEdicion ? 'Producto actualizado' : 'Producto creado');
        this.loadData();

        notificarProducto(
          esEdicion ? 'Producto actualizado' : 'Nuevo producto registrado',
          nombreGuardado,
          `Código: ${codigoGuardado} · $${precioGuardado.toFixed(2)} USD`
        );
      } catch (error) { 
        this.mostrarError(error.message); 
      } finally { 
        this.saving = false; 
      } 
    },
    async deleteProducto(id) { 
      try { 
        const res = await eliminarProducto(id); 
        if (res.success) { await this.loadData(); this.mostrarExito('Eliminado'); notificarProducto('Producto eliminado', `ID: ${id}`, 'Eliminado del inventario'); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError(error.message); } 
    },
    async toggleStatus(producto) { 
      try { 
        const nuevoEstado = producto.estado?.toLowerCase() === 'activo' ? 'Inactivo' : 'Activo'; 
        const res = await cambiarEstadoProducto(producto.idProducto, nuevoEstado); 
        if (res.success) { await this.loadData(); this.mostrarExito(`Producto ${nuevoEstado.toLowerCase()}`); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError('Error'); } 
    },

    async recalcularPrecioIndividual(producto) { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      try { 
        const costoRealUSD = parseFloat(producto.precio_compra_usdt); 
        
        let precioVentaUSD;
        if (producto.tipo_ganancia === 'PORCENTAJE') { 
          precioVentaUSD = costoRealUSD * (1 + ((producto.valor_ganancia || 30) / 100)); 
        } else { 
          precioVentaUSD = costoRealUSD + (producto.valor_ganancia || 15); 
        } 
        
        if (producto.precio_venta_usd > 0 && precioVentaUSD < producto.precio_venta_usd) {
          precioVentaUSD = producto.precio_venta_usd;
        }
        
        const nuevoPrecioUSD = Math.round(precioVentaUSD * 100) / 100; 
        const nuevoPrecioBs = Math.round(nuevoPrecioUSD * (this.tasaBCV?.precio_usd_bs || 1)); 
        
        const res = await actualizarProducto(producto.idProducto, { 
          ...producto, 
          precio_venta_usd: nuevoPrecioUSD, 
          precio_venta_bs: nuevoPrecioBs 
        }); 
        if (res.success) { await this.loadData(); this.mostrarExito('Precio recalculado'); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError(error.message); } 
    },

    async recalcularPreciosMasivos() { 
      this.loading = true; 
      const tasaBCV = this.tasaBCV?.precio_usd_bs || 1; 
      const productos = [...this.filteredProductos];
      
      let exitosos = 0, fallidos = 0; 
      const CONCURRENCIA = 5;

      const calcularData = (p) => {
        const costoRealUSD = parseFloat(p.precio_compra_usdt); 
        let precioVentaUSD;
        if (p.tipo_ganancia === 'PORCENTAJE') { 
          precioVentaUSD = costoRealUSD * (1 + ((p.valor_ganancia || 30) / 100)); 
        } else { 
          precioVentaUSD = costoRealUSD + (p.valor_ganancia || 15); 
        } 
        if (p.precio_venta_usd > 0 && precioVentaUSD < p.precio_venta_usd) {
          precioVentaUSD = p.precio_venta_usd;
        }
        const nuevoPrecioUSD = Math.round(precioVentaUSD * 100) / 100; 
        const nuevoPrecioBs = Math.round(nuevoPrecioUSD * tasaBCV); 
        return { ...p, precio_venta_usd: nuevoPrecioUSD, precio_venta_bs: nuevoPrecioBs };
      };

      for (let i = 0; i < productos.length; i += CONCURRENCIA) {
        const lote = productos.slice(i, i + CONCURRENCIA);
        const resultados = await Promise.allSettled(
          lote.map(p => actualizarProducto(p.idProducto, calcularData(p)))
        );
        resultados.forEach(r => {
          if (r.status === 'fulfilled' && r.value.success) exitosos++;
          else fallidos++;
        });
      }

      await this.loadData(); 
      this.loading = false; 
      if (fallidos > 0) this.mostrarAdvertencia(`✅ ${exitosos} ok, ❌ ${fallidos} error`); 
      else this.mostrarExito(`✅ ${exitosos} actualizados`); 
    },

    async saveStock() { 
      if (!this.stockForm.cantidad || this.stockForm.cantidad <= 0) { this.mostrarError('Cantidad válida'); return; } 
      this.savingStock = true; 
      try { 
        let cantidad = parseFloat(this.stockForm.cantidad); 
        if (this.stockForm.operacion === 'disminuir') cantidad = -cantidad; 
        const res = await actualizarStock(this.stockProducto.idProducto, cantidad, this.stockForm.operacion); 
        if (res.success) { await this.loadData(); this.closeStockModal(); this.mostrarExito('Stock actualizado'); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError(error.message); } 
      finally { this.savingStock = false; } 
    },
    clearFilters() { 
      this.searchQuery = ''; 
      this.filterStatus = 'all'; 
      this.filterStock = 'all'; 
      this.filterProveedor = 'all'; 
      this.sortBy = 'nombre'; 
      this.currentPage = 1; 
      this.loadData(); 
    },
  },
};
</script>

<style>
/* Solución global para los <option> en los modales teletransportados al body */
.dark body select option {
  background-color: #1f2937 !important;
  color: #f9fafb !important;
}
body select option {
  background-color: #ffffff;
  color: #111827;
}
</style>

<style scoped>
/* ========== ESTILOS COMPACTOS ========== */
.productos-container {
  padding: 0.5rem;
}

@media (min-width: 768px) {
  .productos-container {
    padding: 0.75rem;
  }
}

/* ============================================================
   TRANSICIÓN Y ANIMACIONES
   ============================================================ */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

/* ============================================================
   INPUTS MODERNOS
   ============================================================ */
.modern-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  font-size: 0.8125rem;
  color: #0f172a;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.dark .modern-input {
  background: #1e293b;
  border-color: #334155;
  color: #f1f5f9;
}

.modern-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.modern-input::placeholder {
  color: #94a3b8;
}

.dark .modern-input::placeholder {
  color: #64748b;
}

.modern-input option {
  background-color: #ffffff;
  color: #0f172a;
}

.dark .modern-input option {
  background-color: #1e293b;
  color: #f1f5f9;
}

/* ============================================================
   SCROLLBAR
   ============================================================ */
.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

/* ============================================================
   ESTILOS DEL LISTADO (TARJETAS)
   ============================================================ */
.producto-card {
  background-color: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.3s;
}

.dark .producto-card {
  background-color: #1f2937;
  border-color: #374151;
}

.producto-card:hover {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transform: translateY(-1px);
}

.producto-imagen {
  position: relative;
  height: 6rem;
  background: linear-gradient(135deg, #f9fafb, #f3f4f6);
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 640px) {
  .producto-imagen { height: 7rem; }
}

@media (min-width: 768px) {
  .producto-imagen { height: 8rem; }
}

.estado-badge {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  padding: 0.125rem 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  cursor: pointer;
  z-index: 10;
}

.estado-activo { background-color: #d1fae5; color: #065f46; }
.estado-inactivo { background-color: #fee2e2; color: #991b1b; }

.imagen-contenedor {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.imagen-contenedor img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  border-radius: 0.25rem;
}

.imagen-contenedor svg {
  font-size: 2rem;
  color: #d1d5db;
}

.stock-badge {
  position: absolute;
  bottom: 0.25rem;
  left: 0.25rem;
  padding: 0.125rem 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  border-radius: 9999px;
}

.stock-cero { background-color: #fee2e2; color: #991b1b; }
.stock-bajo { background-color: #fed7aa; color: #92400e; }
.stock-normal { background-color: #d1fae5; color: #065f46; }

.producto-info { padding: 0.5rem; }

.producto-codigo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  font-size: 0.625rem;
  background-color: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
  color: #4b5563;
}

.dark .producto-codigo { background-color: #374151; color: #d1d5db; }

.categoria-badge {
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.0625rem 0.375rem;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  font-size: 0.625rem;
}

.dark .categoria-badge { background-color: #1e3a8a; color: #bfdbfe; }

.producto-info h3 {
  font-size: 0.75rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.125rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dark .producto-info h3 { color: #f3f4f6; }

.producto-info > p {
  font-size: 0.625rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.precios {
  margin-bottom: 0.5rem;
  font-size: 0.625rem;
}

.precios > div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.125rem;
}

.precios > div > span:first-child { color: #4b5563; }
.dark .precios > div > span:first-child { color: #9ca3af; }

.precio-venta {
  font-weight: bold;
  color: #10b981;
  font-size: 0.75rem;
}

.precios small {
  font-size: 0.5rem;
  display: block;
  color: #6b7280;
}

.ganancia-positiva { color: #10b981; }
.ganancia-negativa { color: #ef4444; }

.margen {
  background-color: #f9fafb;
  padding: 0.25rem;
  border-radius: 0.25rem;
  margin-top: 0.125rem;
}

.dark .margen { background-color: #374151; }

.margen span:last-child {
  font-weight: 600;
  color: #3b82f6;
}

.producto-detalles {
  margin-bottom: 0.5rem;
  font-size: 0.625rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.producto-detalles div {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.producto-acciones {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

.dark .producto-acciones { border-top-color: #374151; }

.producto-acciones button {
  padding: 0.25rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.producto-acciones button svg { font-size: 0.875rem; }

.btn-editar { background-color: #eff6ff; color: #2563eb; }
.btn-stock { background-color: #fffbeb; color: #d97706; }
.btn-calcular { background-color: #f0fdf4; color: #16a34a; }
.btn-eliminar { background-color: #fef2f2; color: #dc2626; }

.dark .btn-editar { background-color: #1e3a8a; color: #bfdbfe; }
.dark .btn-stock { background-color: #78350f; color: #fde68a; }
.dark .btn-calcular { background-color: #14532d; color: #bbf7d0; }
.dark .btn-eliminar { background-color: #7f1d1d; color: #fecaca; }

/* ============================================================
   LOADING / EMPTY
   ============================================================ */
.loading-spinner {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #3b82f6;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin { to { transform: rotate(360deg); } }

.empty-state {
  padding: 1.5rem;
  text-align: center;
}

.empty-state svg {
  font-size: 2rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}

.empty-state h3 {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.empty-state p {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.empty-state button {
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  border: none;
  background-color: #2563eb;
  color: white;
}

.dark .empty-state h3 { color: #f3f4f6; }
.dark .empty-state p { color: #9ca3af; }

/* Confirm Modal Header Colors */
.confirm-danger .confirm-header { background: linear-gradient(135deg, #dc2626, #b91c1c); }
.confirm-warning .confirm-header { background: linear-gradient(135deg, #f97316, #ea580c); }
.confirm-success .confirm-header { background: linear-gradient(135deg, #10b981, #059669); }
.confirm-blue .confirm-header { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.confirm-amber .confirm-header { background: linear-gradient(135deg, #f59e0b, #d97706); }

</style>